import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import {
	useBarrelLayout,
	useCartridgeOptions,
	useCommonStands,
	useRollsetDetail,
	useRollsetsPrepared,
	useUndoPrepareRollForLine,
} from "../hooks/useRollsCartridge";
import {
	assignmentStatusName,
	computeAssignmentStatus,
	type PreparedStandRow,
	type RollsetPreparedRow,
	type RollsetSearchResult,
	type RollsetView,
	type Stand,
} from "../models/RollsCartridge";
import { useOperationalWritesLocked } from "@/shared/hooks/useOperationalWritesLocked";
import { urlParam, useUrlState } from "@/shared/hooks/useUrlState";
import { RollsCartridgeToolbar } from "./RollsCartridgeToolbar";
import { PrepareRollsetModal } from "./PrepareRollsetModal";
import { RollsetDetailPanel } from "./RollsetDetailPanel";
import { RollsetsPreparedGrid } from "./RollsetsPreparedGrid";

// Left = detail, Right = master grid (Mario's preferred layout)
const DEFAULT_LAYOUT = { left: 45, right: 55 };

const buildRows = (
	rollsets: RollsetPreparedRow[],
	stands: Stand[],
): PreparedStandRow[] => {
	const standById = new Map<number, Stand>();
	for (const s of stands) standById.set(s.Id, s);

	// Group prepared rollsets by stand. Sort by Timestamp ASC so the oldest
	// is the "primary" row and the rest become secondary (greyed).
	const byStand = new Map<number, RollsetPreparedRow[]>();
	for (const r of rollsets) {
		if (r.PreparedStandId == null) continue;
		const list = byStand.get(r.PreparedStandId) ?? [];
		list.push(r);
		byStand.set(r.PreparedStandId, list);
	}

	for (const list of byStand.values()) {
		list.sort((a, b) => {
			const aTs = a.Timestamp ?? "";
			const bTs = b.Timestamp ?? "";
			return aTs < bTs ? -1 : aTs > bTs ? 1 : 0;
		});
	}

	const result: PreparedStandRow[] = [];

	const orderedStands = [...stands].sort(
		(a, b) => a.StandNumber - b.StandNumber,
	);

	for (const stand of orderedStands) {
		const list = byStand.get(stand.Id);
		if (!list || list.length === 0) {
			result.push({
				Id: -stand.Id,
				Name: null,
				StatusCode: null,
				StatusName: null,
				CartridgeId: null,
				CartridgeName: null,
				BarrelId: null,
				TopRollId: null,
				TopRollName: null,
				TopRollDiameter: null,
				BottomRollId: null,
				BottomRollName: null,
				BottomRollDiameter: null,
				Diameter: null,
				PreparedStandId: stand.Id,
				StarterPassId: null,
				StarterPassName: null,
				StarterPassPosition: null,
				Timestamp: null,
				Description: null,
				MillSizeId: null,
				PassId: null,
				Comments: null,
				StandName: stand.Name,
				StandOrder: stand.StandOrder,
				IsPlaceholder: true,
				IsSecondary: false,
				AlternatesCount: 0,
				HasAlternates: false,
			});
			continue;
		}

		const alternatesCount = list.length;
		list.forEach((row, idx) => {
			result.push({
				...row,
				StandName: stand.Name,
				StandOrder: stand.StandOrder,
				IsPlaceholder: false,
				IsSecondary: idx > 0,
				AlternatesCount: alternatesCount,
				HasAlternates: idx === 0 && alternatesCount > 1,
			});
		});
	}

	// Any rollset with a PreparedStandId not in the stand list (edge case)
	for (const [preparedStandId, list] of byStand.entries()) {
		if (standById.has(preparedStandId)) continue;
		const alternatesCount = list.length;
		list.forEach((row, idx) => {
			result.push({
				...row,
				StandName: `Stand #${preparedStandId}`,
				StandOrder: 9999,
				IsPlaceholder: false,
				IsSecondary: idx > 0,
				AlternatesCount: alternatesCount,
				HasAlternates: idx === 0 && alternatesCount > 1,
			});
		});
	}

	return result;
};

const URL_SCHEMA = {
	rollsetId: urlParam.int(),
};

export const RollsCartridge = () => {
	const writesLocked = useOperationalWritesLocked();
	// The two filters and the master grid are three ways of choosing the same
	// thing: the rollset the detail panel shows. Only the id is shared state,
	// and it is the id that travels in the URL.
	const [{ rollsetId: selectedRollsetId }, setFilters, { dropValues }] =
		useUrlState(URL_SCHEMA);
	const [filterRollset, setFilterRollset] =
		useState<RollsetSearchResult | null>(null);
	const [cartridgeId, setCartridgeId] = useState<number | null>(null);
	const [prepareModalOpen, setPrepareModalOpen] = useState(false);
	const [errorMsg, setErrorMsg] = useState("");
	// Local override of the operational status, applied immediately after a
	// successful Prepare / Undo mutation so the action button flips without
	// waiting for the refetch to land.
	const [opStatusOverride, setOpStatusOverride] = useState<{
		code: string;
		name: string;
	} | null>(null);
	// Set of stand IDs whose alternate rollsets are currently expanded in the
	// master grid. Stands with only one prepared rollset are never in this set.
	const [expandedStands, setExpandedStands] = useState<Set<number>>(
		() => new Set(),
	);

	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "rolls-cartridge-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const rollsetsQuery = useRollsetsPrepared();
	const standsQuery = useCommonStands();
	const cartridgesQuery = useCartridgeOptions();
	const barrelLayoutQuery = useBarrelLayout(selectedRollsetId);
	const rollsetDetailQuery = useRollsetDetail(selectedRollsetId);

	const rows = useMemo(
		() => buildRows(rollsetsQuery.data ?? [], standsQuery.data ?? []),
		[rollsetsQuery.data, standsQuery.data],
	);

	// Show primary rows always; hide secondary rows unless the user has
	// expanded that stand (legacy UX: collapsed by default).
	const visibleRows = useMemo(
		() =>
			rows.filter((r) => {
				if (!r.IsSecondary) return true;
				if (r.PreparedStandId == null) return false;
				return expandedStands.has(r.PreparedStandId);
			}),
		[rows, expandedStands],
	);

	const handleToggleExpandStand = useCallback((standId: number) => {
		setExpandedStands((prev) => {
			const next = new Set(prev);
			if (next.has(standId)) next.delete(standId);
			else next.add(standId);
			return next;
		});
	}, []);

	// The grid row of the selected rollset, when it has one: a rollset reached
	// through a filter can be available or already in use, and neither is in
	// this list.
	const selectedGridRow = useMemo(
		() =>
			selectedRollsetId == null
				? null
				: (rows.find((r) => !r.IsPlaceholder && r.Id === selectedRollsetId) ??
					null),
		[rows, selectedRollsetId],
	);

	const handleRowClick = useCallback(
		(row: PreparedStandRow) => {
			setFilters({ rollsetId: row.Id });
			setFilterRollset(null);
			setCartridgeId(null);
		},
		[setFilters],
	);

	// A link can point at a rollset the prepared list does not hold: one reached through the
	// typeahead can be available or already in use. Rebuild the label from the detail so the
	// toolbar does not come back blank.
	useEffect(() => {
		if (selectedRollsetId === null || selectedGridRow) return;
		if (filterRollset?.Id === selectedRollsetId) return;
		const detail = rollsetDetailQuery.data;
		if (!detail || detail.Id !== selectedRollsetId) return;
		setFilterRollset({
			Id: detail.Id,
			Name: detail.Name,
			StatusCode: detail.StatusCode,
			StatusName: detail.StatusName,
			StandNumber: detail.StandNumber,
			StandName: detail.StandName,
			TopRollName: detail.TopRollName,
			BottomRollName: detail.BottomRollName,
			CartridgeId: null,
			CartridgeName: null,
		});
	}, [
		selectedRollsetId,
		selectedGridRow,
		filterRollset,
		rollsetDetailQuery.data,
	]);

	// A link to a rollset that no longer exists leaves the panel empty, so the key goes.
	useEffect(() => {
		if (selectedRollsetId === null) return;
		if (rollsetDetailQuery.isSuccess && !rollsetDetailQuery.data)
			dropValues("rollsetId");
	}, [
		selectedRollsetId,
		rollsetDetailQuery.isSuccess,
		rollsetDetailQuery.data,
		dropValues,
	]);

	// The two filters are mutually exclusive and each one opens its rollset
	// straight away: no apply step, and no effect on the list on the right.
	const handleRollsetFilterChange = useCallback(
		(value: RollsetSearchResult | null) => {
			setFilterRollset(value);
			if (value) {
				setCartridgeId(null);
				setFilters({ rollsetId: value.Id });
			}
		},
		[setFilters],
	);

	const handleCartridgeFilterChange = useCallback(
		(value: number | null) => {
			setCartridgeId(value);
			if (value == null) return;
			setFilterRollset(null);
			const cartridge = (cartridgesQuery.data ?? []).find(
				(c) => c.Id === value,
			);
			setFilters({ rollsetId: cartridge?.RollsetId ?? null });
		},
		[cartridgesQuery.data, setFilters],
	);

	// Drop the op-status override whenever the user selects a different rollset
	// so we fall back to the live/grid data for the fresh selection.
	useEffect(() => {
		setOpStatusOverride(null);
	}, [selectedRollsetId]);

	const handleReset = useCallback(() => {
		setFilterRollset(null);
		setCartridgeId(null);
		dropValues("rollsetId");
		rollsetsQuery.refetch();
		cartridgesQuery.refetch();
	}, [rollsetsQuery, cartridgesQuery, dropValues]);

	const { mutate: undoPrepare, isPending: isUndoing } =
		useUndoPrepareRollForLine({
			onSuccess: () => {
				setOpStatusOverride({ code: "AVAILABLE", name: "Available" });
			},
			onError: (err) => setErrorMsg(err.message),
		});

	const handleUndoPrepare = useCallback(() => {
		if (selectedRollsetId == null) return;
		undoPrepare({ rollsetId: selectedRollsetId });
	}, [selectedRollsetId, undoPrepare]);

	const handleOpenPrepareModal = useCallback(() => {
		setPrepareModalOpen(true);
	}, []);

	const handleClosePrepareModal = useCallback(() => {
		setPrepareModalOpen(false);
	}, []);

	const handlePrepareSuccess = useCallback(() => {
		setOpStatusOverride({ code: "PREPAREDFORLINE", name: "Prepared for line" });
		setPrepareModalOpen(false);
	}, []);

	const firstError =
		rollsetsQuery.error ?? standsQuery.error ?? cartridgesQuery.error;

	if (firstError && !errorMsg) {
		setErrorMsg((firstError as Error).message);
	}

	const isLoading = rollsetsQuery.isFetching || standsQuery.isLoading;

	const liveDetail = rollsetDetailQuery.data ?? null;

	// One rollset, two possible sources. The procedure is authoritative; the
	// grid row carries the same rollset and fills the panel while the detail
	// is still in flight.
	const selectedRollset = useMemo<RollsetView | null>(() => {
		if (selectedRollsetId == null) return null;
		if (!liveDetail && !selectedGridRow) return null;
		return {
			Id: selectedRollsetId,
			Name: liveDetail?.Name ?? selectedGridRow?.Name ?? null,
			StandName: liveDetail?.StandName ?? selectedGridRow?.StandName ?? null,
			CartridgeName:
				liveDetail?.CartridgeName ?? selectedGridRow?.CartridgeName ?? null,
			TopRollName:
				liveDetail?.TopRollName ?? selectedGridRow?.TopRollName ?? null,
			BottomRollName:
				liveDetail?.BottomRollName ?? selectedGridRow?.BottomRollName ?? null,
			TopRollDiameter:
				liveDetail?.TopRollDiameter ?? selectedGridRow?.TopRollDiameter ?? null,
			BottomRollDiameter:
				liveDetail?.BottomRollDiameter ??
				selectedGridRow?.BottomRollDiameter ??
				null,
			BarrelName: liveDetail?.BarrelName ?? liveDetail?.Description ?? null,
			Timestamp:
				liveDetail?.PreparedForLineTimestampString ??
				selectedGridRow?.Timestamp ??
				null,
		};
	}, [selectedRollsetId, liveDetail, selectedGridRow]);

	// catRollsets.StandId is not populated, so the stand a rollset was built
	// for has to be resolved through its number, the way the procedures do.
	const selectedRollsetStandId = useMemo(() => {
		const standNumber = liveDetail?.StandNumber;
		if (standNumber == null) return null;
		return (
			(standsQuery.data ?? []).find((s) => s.StandNumber === standNumber)?.Id ??
			null
		);
	}, [liveDetail?.StandNumber, standsQuery.data]);

	// Derive the two statuses shown in the detail panel
	const assignmentStatus = selectedRollset
		? computeAssignmentStatus(
				selectedRollset.TopRollName,
				selectedRollset.BottomRollName,
			)
		: null;
	// Op status priority:
	//   1. opStatusOverride — local state set right after a successful mutation,
	//      so the button flips before the refetch lands.
	//   2. liveDetail       — refetch from GetRollsets.
	//   3. the grid row     — always PREPAREDFORLINE while it is visible there.
	const opStatusCode =
		opStatusOverride?.code ??
		liveDetail?.StatusCode ??
		selectedGridRow?.StatusCode ??
		null;
	const opStatusName =
		opStatusOverride?.name ??
		liveDetail?.StatusName ??
		selectedGridRow?.StatusName ??
		null;

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Rolls Cartridge
			</Typography>
			<Paper elevation={3}>
				<RollsCartridgeToolbar
					rollset={filterRollset}
					onRollsetChange={handleRollsetFilterChange}
					cartridgeId={cartridgeId}
					onCartridgeChange={handleCartridgeFilterChange}
					cartridges={cartridgesQuery.data ?? []}
					onReset={handleReset}
					isLoading={isLoading}
				/>
			</Paper>

			<Group
				id="rolls-cartridge-group"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel id="left" minSize={25} style={{ paddingBottom: 5 }}>
					<Box sx={{ height: "100%", pr: 1 }}>
						<Paper
							elevation={3}
							sx={{
								height: "100%",
								overflow: "hidden",
							}}
						>
							<RollsetDetailPanel
								rollset={selectedRollset}
								assignmentStatus={assignmentStatus}
								assignmentStatusLabel={
									assignmentStatus
										? assignmentStatusName(assignmentStatus)
										: null
								}
								opStatusCode={opStatusCode}
								opStatusName={opStatusName}
								barrelLayout={barrelLayoutQuery.data ?? []}
								isLoadingLayout={barrelLayoutQuery.isFetching}
								isDetailLoading={rollsetDetailQuery.isFetching && !liveDetail}
								onPrepareClick={handleOpenPrepareModal}
								onUndoPrepareClick={handleUndoPrepare}
								isMutating={isUndoing}
								writeDisabled={writesLocked}
							/>
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 8,
							height: 48,
							borderRadius: 1,
							backgroundColor: "#e0e0e0",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							gap: "3px",
							transition: "background-color 0.2s",
							"&:hover": {
								backgroundColor: "primary.main",
								"& .grip-dot": { backgroundColor: "#fff" },
							},
						}}
					>
						{[...Array(5)].map((_, i) => (
							<Box
								key={`cartridge-grip-${i}`}
								className="grip-dot"
								sx={{
									width: 4,
									height: 4,
									borderRadius: "50%",
									backgroundColor: "#9e9e9e",
									transition: "background-color 0.2s",
								}}
							/>
						))}
					</Box>
				</Separator>

				<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ height: "100%", pl: 1 }}>
						<Paper
							elevation={3}
							sx={{
								height: "100%",
								overflow: "hidden",
							}}
						>
							<RollsetsPreparedGrid
								rows={visibleRows}
								selectedRollsetId={selectedRollsetId}
								onRowClick={handleRowClick}
								expandedStands={expandedStands}
								onToggleExpandStand={handleToggleExpandStand}
								isLoading={isLoading}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			{prepareModalOpen && selectedRollsetId != null && (
				<PrepareRollsetModal
					rollsetId={selectedRollsetId}
					defaultStandId={selectedRollsetStandId}
					onClose={handleClosePrepareModal}
					onSuccess={handlePrepareSuccess}
					onError={(msg) => setErrorMsg(msg)}
				/>
			)}

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
